import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  LinkOff as LinkOffIcon,
  Lock as LockIcon,
  OpenInNew as OpenInNewIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Save as SaveIcon,
  SettingsSuggest as SettingsSuggestIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import apiManagerApi, { apiOAuthApi } from './api';
import LiveIngestPanel from './LiveIngestPanel';
import ConnectorSyncCard, { type ConnectorMeta } from './ConnectorSyncCard';
import {
  DEFAULT_INGEST_LANES,
  INGEST_LANE_LABEL,
  type IngestLane,
  laneChipColor,
  leftoverOwnerChipLabel,
  matchesIngestLanes,
  applyHonestIngestRuntime,
  presentAuthChip,
  presentPipelineKpi,
  presentPipelineLastError,
  presentVisibleLastError,
} from './ingestHonesty';
import {
  buildApiKeySavePayload,
  buildIngestOauthRedirectUri,
  buildOauthClientCredentialsSavePayload,
  canSaveWithoutAuthorizeUrl,
  credentialStatusLabel,
  emptyCredentialForm,
  ingestOauthRedirectUriForCard,
  oauthPollSettled,
  presentSecretPreview,
  type CredentialFormState,
  type CredentialPresence,
} from './ingestCredentialForm';
import {
  MET_NORWAY_USER_AGENT,
  buildUserAgentSavePayload,
  defaultMetUserAgent,
  looksLikeMetNorway,
  setupStatusLabel,
  type RequestHeaderPresence,
} from './ingestRequestHeaders';
import { isHealthPingQuery, queryToRows, rowsToQuery, type QueryRow } from './pipelineQueryEditor';
import { composeWorkspaceEndpointUrl } from './endpointPathEdit';
import ApiRegistryCards from './ApiRegistryCards';
import { CREDENTIAL_STATE_LABEL, ON_DEMAND_LABEL, isOnDemand, type RegistryFacts, type TriggerMode } from './apiRegistryView';
import { useSelectedAppId, useSelectedAppName } from '@/hooks/useSelectedAppId';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { listAppInstances } from '@/features/apps/api';
import { useAppStore } from '@/store/appStore';

const STORE_MODES = ['telemetry', 'api_telemetry', 'both', 'pim', 'domain_events'];

type WorkspaceApi = {
  api_id: string;
  api_name: string;
  provider?: string;
  base_url?: string;
  auth_type?: string;
  status?: string;
  description?: string;
  leftover_owner?: string | null;
  app_id?: string;
  created_at?: string | null;
  trigger_mode?: TriggerMode;
  registry?: RegistryFacts;
  endpoints?: any[];
  workspace?: {
    group_key?: string | null;
    group_override?: string | null;
    effective_group?: string | null;
    tags?: string[];
    owner?: string | null;
    criticality?: string | null;
    notes?: string | null;
  };
  runtime?: {
    pipeline_count?: number;
    enabled_pipeline_count?: number;
    latest_ingest_at?: string | null;
    last_attempt_at?: string | null;
    last_error?: string | null;
    credential_presence?: { missing_reason?: string | null } | null;
    failed_count_window?: number;
    published_count_window?: number;
    records_24h?: number;
    census_store?: string;
    uses_pipeline_objects?: boolean;
    health?: string | null;
    lane?: IngestLane;
    lane_reason?: string;
    registry_status?: string | null;
  };
};

type WorkspaceDetail = WorkspaceApi & {
  ingest_session?: { session_id: string; phase?: string | null; updated_at?: string | null } | null;
  classification_template_ids?: string[];
  endpoints?: any[];
  pipelines?: any[];
  connections?: any[];
  mappings?: any[];
  history?: any[];
  credentials?: {
    auth_type?: string;
    grant_type?: string;
    client_id?: string;
    token_url?: string;
    authorization_url?: string;
    api_key?: string;
    client_secret?: string;
    vault_path?: string;
  } | null;
  credential_presence?: CredentialPresence | null;
  updated_at?: string | null;
  request_headers?: RequestHeaderPresence | null;
  provider_metadata?: {
    default_headers?: Record<string, string>;
    api_ingest?: ConnectorMeta;
  } | null;
};

type ApiWorkspacePanelProps = {
  onResumeApi: (apiId: string) => void;
  onOpenCreateAssistant: () => void;
  onPipelineSelect?: (pipelineId: string | null) => void;
};

function formatDateTime(value?: string | null) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
}

function cleanTags(text: string) {
  return text
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);
}

export default function ApiWorkspacePanel({
  onResumeApi,
  onOpenCreateAssistant,
  onPipelineSelect,
}: ApiWorkspacePanelProps) {
  const scopedAppId = useSelectedAppId();
  const scopedAppName = useSelectedAppName();
  const setApp = useAppStore((s) => s.setApp);
  const [searchParams] = useSearchParams();
  const deepLinkApiId = String(searchParams.get('api') || '').trim();
  const openedOnAllApps = String(searchParams.get('scope') || '') === 'all';
  const [allApps, setAllApps] = useState(openedOnAllApps);
  const [addApiOpen, setAddApiOpen] = useState(false);
  const [addApiAppId, setAddApiAppId] = useState('');
  const [pipelineDraft, setPipelineDraft] = useState<Record<string, { every_seconds: string; store_mode: string }>>({});
  const [pipelineSaving, setPipelineSaving] = useState<string | null>(null);
  const [lastReceipt, setLastReceipt] = useState<string | null>(null);
  const appsQuery = useQuery({
    queryKey: ['app-instances', 'switcher-list'],
    queryFn: listAppInstances,
    staleTime: 10_000,
    enabled: addApiOpen,
  });
  const [apis, setApis] = useState<WorkspaceApi[]>([]);
  const [groups, setGroups] = useState<Array<{ name: string; count: number }>>([]);
  const [detail, setDetail] = useState<WorkspaceDetail | null>(null);
  const [selectedApiId, setSelectedApiId] = useState<string | null>(null);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [laneFilter, setLaneFilter] = useState<IngestLane[] | ['all']>(openedOnAllApps ? ['all'] : [...DEFAULT_INGEST_LANES]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    api_name: '',
    provider: '',
    base_url: '',
    status: 'active',
    group_override: '',
    tags: '',
    owner: '',
    criticality: '',
    notes: '',
  });

  const [credentialForm, setCredentialForm] = useState<CredentialFormState>(emptyCredentialForm());
  const [credentialSaving, setCredentialSaving] = useState(false);
  const [credentialMessage, setCredentialMessage] = useState<string | null>(null);
  const [oauthConnecting, setOauthConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [userAgent, setUserAgent] = useState('');
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerMessage, setHeaderMessage] = useState<string | null>(null);
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [queryRows, setQueryRows] = useState<QueryRow[]>(queryToRows({}));
  const [querySaving, setQuerySaving] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewIngestId, setViewIngestId] = useState<string | null>(null);
  const [viewObservation, setViewObservation] = useState<{
    ingest_id?: string | null;
    present?: boolean;
    source?: string | null;
    body?: unknown;
    empty_reason?: string | null;
    empty_message?: string | null;
    note?: string | null;
    pulsar_receipt?: unknown;
    timescale?: unknown;
  } | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const [pathEdit, setPathEdit] = useState<{ endpoint_id: string; path: string } | null>(null);
  const [pathSaving, setPathSaving] = useState(false);
  const lastErrorLabel = presentVisibleLastError(detail?.runtime, detail?.status);
  const ingestOauthRedirectUri = useMemo(
    () => ingestOauthRedirectUriForCard({
      authType: detail?.auth_type,
      grantType: credentialForm.grant_type,
      origin: typeof window !== 'undefined' ? window.location.origin : '',
    }),
    [detail?.auth_type, credentialForm.grant_type],
  );

  const laneCounts = useMemo(() => {
    const counts: Record<IngestLane | 'all', number> = {
      all: apis.length,
      live: 0,
      broken: 0,
      pending_auth: 0,
      retired: 0,
    };
    for (const api of apis) {
      const lane = api.runtime?.lane;
      if (lane && counts[lane] != null) counts[lane] += 1;
    }
    return counts;
  }, [apis]);

  const visibleApis = useMemo(
    () => apis.filter((api) => matchesIngestLanes(api.runtime?.lane, laneFilter)),
    [apis, laneFilter]
  );

  const groupedApis = useMemo(() => {
    const bucket = new Map<string, WorkspaceApi[]>();
    for (const api of visibleApis) {
      const key = api.workspace?.effective_group || 'General';
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key)!.push(api);
    }
    return Array.from(bucket.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleApis]);

  const selectedApi = useMemo(
    () => apis.find((api) => String(api.api_id) === String(selectedApiId || '')) || null,
    [apis, selectedApiId]
  );

  const loadApis = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiManagerApi.listWorkspaceApis({
        search: search || undefined,
        group: groupFilter !== 'all' ? groupFilter : undefined,
        window_minutes: 1440,
        ...(allApps ? { scope: 'all' as const } : { app_id: scopedAppId || undefined }),
      });
      const nextApis = (response.data?.apis || []).map((api: WorkspaceApi) => applyHonestIngestRuntime(api));
      setApis(nextApis);
      setGroups(response.data?.groups || []);
      const deepLinked = deepLinkApiId
        ? nextApis.find((api: WorkspaceApi) => api.api_id === deepLinkApiId)
        : null;
      // A just-installed connector is pending (not live/broken); widen the list so it is visible.
      if (deepLinked && !matchesIngestLanes(deepLinked.runtime?.lane, laneFilter)) setLaneFilter(['all']);
      setSelectedApiId((current) => {
        if (deepLinked) return deepLinkApiId;
        if (allApps) {
          return current && nextApis.some((api: WorkspaceApi) => api.api_id === current) ? current : null;
        }
        if (current && nextApis.some((api: WorkspaceApi) => api.api_id === current)) return current;
        const preferred = nextApis.find((api: WorkspaceApi) => matchesIngestLanes(api.runtime?.lane, DEFAULT_INGEST_LANES));
        return preferred?.api_id || nextApis[0]?.api_id || null;
      });
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load API workspace');
      setApis([]);
      setGroups([]);
      setSelectedApiId(null);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (apiId: string) => {
    try {
      setDetailLoading(true);
      const response = await apiManagerApi.getWorkspaceApiDetail(apiId, { history_limit: 25 });
      const rawDetail = response.data?.detail || null;
      const nextDetail = rawDetail ? applyHonestIngestRuntime(rawDetail) : null;
      // Live walk: workspace detail history[] can be empty while
      // GET /cp/api-ingest/api/ingest/history?api_id= returns metadata rows.
      if (nextDetail && (!Array.isArray(nextDetail.history) || nextDetail.history.length === 0)) {
        try {
          const list = await apiManagerApi.ingestHistory({ api_id: apiId, limit: 25 });
          const rows = list.data?.history || [];
          if (rows.length) nextDetail.history = rows;
        } catch {
          // Keep the empty detail history; the accordion already says so.
        }
      }
      setDetail(nextDetail);
      const defaultPipelineId = nextDetail?.pipelines?.[0]?.pipeline_id || null;
      setSelectedPipelineId(defaultPipelineId);
      onPipelineSelect?.(defaultPipelineId);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load API detail');
      setDetail(null);
      setSelectedPipelineId(null);
      onPipelineSelect?.(null);
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    loadApis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupFilter, scopedAppId, deepLinkApiId, allApps]);

  const handleScopeChange = (next: 'app' | 'all') => {
    const wide = next === 'all';
    setAllApps(wide);
    setLaneFilter(wide ? ['all'] : [...DEFAULT_INGEST_LANES]);
    if (wide && !deepLinkApiId) setSelectedApiId(null);
  };

  const handleAddApi = () => {
    if (!allApps) {
      onOpenCreateAssistant();
      return;
    }
    setAddApiAppId(scopedAppId || '');
    setAddApiOpen(true);
  };

  const confirmAddApi = () => {
    const apps = Array.isArray(appsQuery.data?.apps) ? appsQuery.data!.apps : [];
    const chosen = apps.find((app: any) => app.app_id === addApiAppId);
    if (!chosen) return;
    setApp({ appId: chosen.app_id, appName: chosen.name || null });
    setAddApiOpen(false);
    handleScopeChange('app');
    onOpenCreateAssistant();
  };

  const draftFor = (pipe: any) => pipelineDraft[pipe.pipeline_id] || {
    every_seconds: String(pipe.schedule?.every_seconds || ''),
    store_mode: String(pipe.store_mode || 'telemetry'),
  };

  const savePipeline = async (
    pipelineId: string,
    body: { enabled?: boolean; schedule?: { every_seconds: number }; store_mode?: string },
  ) => {
    setPipelineSaving(pipelineId);
    try {
      setError(null);
      const resp = await apiManagerApi.updatePipeline(pipelineId, body);
      setLastReceipt(resp.data?.door_log_id || null);
      setPipelineDraft((current) => {
        const next = { ...current };
        delete next[pipelineId];
        return next;
      });
      if (detail?.api_id) await Promise.all([loadApis(), loadDetail(detail.api_id)]);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update pipeline');
    } finally {
      setPipelineSaving(null);
    }
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      loadApis();
    }, 250);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (selectedApiId) loadDetail(selectedApiId);
    else {
      setDetail(null);
      setSelectedPipelineId(null);
      onPipelineSelect?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApiId]);

  const openEditDialog = () => {
    if (!detail) return;
    setEditForm({
      api_name: detail.api_name || '',
      provider: detail.provider || '',
      base_url: detail.base_url || '',
      status: detail.status || 'active',
      group_override: detail.workspace?.group_override || '',
      tags: (detail.workspace?.tags || []).join(', '),
      owner: detail.workspace?.owner || '',
      criticality: detail.workspace?.criticality || '',
      notes: detail.workspace?.notes || '',
    });
    setEditOpen(true);
  };

  const handleSaveMetadata = async () => {
    if (!detail?.api_id) return;
    try {
      setSaving(true);
      setError(null);
      await apiManagerApi.updateWorkspaceMeta(detail.api_id, {
        api: {
          api_name: editForm.api_name,
          provider: editForm.provider,
          base_url: editForm.base_url,
          status: editForm.status,
        },
        metadata: {
          group_override: editForm.group_override || null,
          tags: cleanTags(editForm.tags),
          owner: editForm.owner || null,
          criticality: editForm.criticality || null,
          notes: editForm.notes || null,
        },
      });
      setEditOpen(false);
      await Promise.all([loadApis(), loadDetail(detail.api_id)]);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save API metadata');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteApi = async () => {
    if (!detail?.api_id) return;
    if (!window.confirm(`Delete API "${detail.api_name}"? This removes the registry entry and stops its pipelines.`)) return;
    try {
      await apiManagerApi.deleteApi(detail.api_id);
      setDetail(null);
      setSelectedPipelineId(null);
      onPipelineSelect?.(null);
      await loadApis();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete API');
    }
  };

  const handleDeleteConnection = async (connectionId: string) => {
    if (!window.confirm('Delete this connection?')) return;
    try {
      await apiManagerApi.deleteConnection(connectionId);
      if (detail?.api_id) await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete connection');
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await apiManagerApi.deleteMapping(mappingId);
      if (detail?.api_id) await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to delete mapping');
    }
  };

  const selectedPipeline = useMemo(
    () => (detail?.pipelines || []).find((pipe: any) => pipe.pipeline_id === selectedPipelineId) || null,
    [detail, selectedPipelineId]
  );

  useEffect(() => {
    setQueryRows(queryToRows(selectedPipeline?.request?.query || {}));
  }, [selectedPipeline?.pipeline_id, selectedPipeline?.updated_at]);

  const handleSaveEndpointPath = async () => {
    if (!detail?.api_id || !pathEdit?.endpoint_id) return;
    const nextPath = pathEdit.path.trim();
    if (!nextPath.startsWith('/')) {
      setError('Endpoint path must start with / and must not be a full URL.');
      return;
    }
    setPathSaving(true);
    try {
      setError(null);
      const resp = await apiManagerApi.updateEndpointPath(detail.api_id, pathEdit.endpoint_id, { path: nextPath });
      setLastReceipt(resp.data?.door_log_id || null);
      setPathEdit(null);
      await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save endpoint path');
    } finally {
      setPathSaving(false);
    }
  };

  const handleSavePipelineQuery = async () => {
    if (!selectedPipelineId) return;
    const query = rowsToQuery(queryRows);
    if (isHealthPingQuery(query)) {
      setError('That lat/lon pair is the health-ping default, not instance data. Enter the app instance coordinates.');
      return;
    }
    setQuerySaving(true);
    try {
      await apiManagerApi.updatePipelineQuery(selectedPipelineId, { query });
      if (detail?.api_id) await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save instance query');
    } finally {
      setQuerySaving(false);
    }
  };

  const handleRunPipeline = async (pipelineId: string, runKind: 'health_ping' | 'instance_ingest' = 'health_ping') => {
    try {
      const resp = await apiManagerApi.runPipeline(pipelineId, { run_kind: runKind });
      setLastReceipt(resp.data?.door_log_id || null);
      if (detail?.api_id) await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to run pipeline');
    }
  };

  const handleReplay = async (ingestId: string) => {
    try {
      await apiManagerApi.replayIngest(ingestId);
      if (detail?.api_id) await loadDetail(detail.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to replay ingest');
    }
  };

  const handleViewObservation = async (ingestId: string) => {
    setViewOpen(true);
    setViewIngestId(ingestId);
    setViewLoading(true);
    setViewError(null);
    setViewObservation(null);
    try {
      const response = await apiManagerApi.ingestHistoryItem(ingestId);
      const observation = response.data?.observation || null;
      if (observation) {
        setViewObservation(observation);
      } else {
        setViewObservation({
          ingest_id: ingestId,
          present: false,
          body: null,
          empty_reason: 'envelope_absent',
          empty_message:
            'No observation body is persisted on this history record. The store kept ingest metadata and (when present) a Pulsar receipt only.',
          pulsar_receipt: response.data?.item?.results?.pulsar ?? null,
          timescale: response.data?.item?.results?.timescale ?? null,
        });
      }
    } catch (err: any) {
      setViewError(err.response?.data?.error || err.message || 'Failed to load history record');
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    setCredentialForm(emptyCredentialForm({
      authType: detail?.auth_type,
      provider: detail?.provider,
      grantType: detail?.credentials?.grant_type || detail?.credential_presence?.grant_type,
      tokenUrl: detail?.credentials?.token_url,
      authorizationUrl: detail?.credentials?.authorization_url,
      clientId: detail?.credentials?.client_id,
    }));
    setOauthError(null);
    setCredentialMessage(null);
    setShowClientSecret(false);
    setShowApiKey(false);
    setUserAgent(defaultMetUserAgent({
      provider: detail?.provider,
      apiName: detail?.api_name,
      apiId: detail?.api_id,
      baseUrl: detail?.base_url,
      persisted: detail?.request_headers?.user_agent || detail?.provider_metadata?.default_headers?.['User-Agent'],
    }));
    setHeaderMessage(null);
    setHeaderError(null);
  }, [selectedApiId, detail?.api_id, detail?.auth_type, detail?.provider, detail?.api_name, detail?.base_url, detail?.credentials?.grant_type, detail?.credentials?.token_url, detail?.credentials?.authorization_url, detail?.credential_presence?.grant_type, detail?.request_headers?.user_agent]);

  const handleSaveHeaders = async () => {
    if (!detail?.api_id) return;
    try {
      setHeaderSaving(true);
      setHeaderError(null);
      setHeaderMessage(null);
      const payload = buildUserAgentSavePayload(userAgent || MET_NORWAY_USER_AGENT);
      const resp = await apiManagerApi.saveApiHeaders(detail.api_id, payload);
      setHeaderMessage(resp.data?.message || 'User-Agent stored.');
      await Promise.all([loadApis(), loadDetail(detail.api_id)]);
    } catch (err: any) {
      setHeaderError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save User-Agent');
    } finally {
      setHeaderSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!detail?.api_id) return;
    const authType = String(detail.auth_type || '').toLowerCase();
    try {
      setCredentialSaving(true);
      setOauthError(null);
      setCredentialMessage(null);
      let payload: Record<string, unknown>;
      if (authType === 'api_key' || authType === 'bearer') {
        if (authType === 'api_key' && !canSaveWithoutAuthorizeUrl(credentialForm, 'api_key', detail.credential_presence)) {
          setOauthError('Enter an API key to save.');
          return;
        }
        payload = authType === 'bearer'
          ? { auth_type: 'bearer', token: credentialForm.api_key.trim() }
          : buildApiKeySavePayload(credentialForm);
      } else if (authType === 'oauth2' && credentialForm.grant_type === 'client_credentials') {
        if (!canSaveWithoutAuthorizeUrl(credentialForm, 'oauth2', detail.credential_presence)) {
          setOauthError('Client ID and Client Secret are required.');
          return;
        }
        payload = buildOauthClientCredentialsSavePayload(credentialForm);
      } else if (authType === 'oauth2') {
        if (!credentialForm.client_id.trim() || !credentialForm.client_secret.trim()) {
          setOauthError('Client ID and Client Secret are required.');
          return;
        }
        payload = {
          auth_type: 'oauth2',
          grant_type: 'authorization_code',
          client_id: credentialForm.client_id.trim(),
          client_secret: credentialForm.client_secret.trim(),
          token_url: credentialForm.token_url.trim() || undefined,
          authorization_url: credentialForm.authorization_url.trim() || undefined,
          provider: credentialForm.provider.trim() || detail.provider || undefined,
        };
      } else {
        setOauthError(`No credential form for auth type ${authType || 'unknown'}.`);
        return;
      }
      const resp = await apiManagerApi.saveApiCredentials(detail.api_id, payload);
      setCredentialMessage(resp.data?.message || 'Credentials stored.');
      setCredentialForm((prev) => ({
        ...prev,
        api_key: '',
        client_secret: '',
        x_api_key: '',
      }));
      await Promise.all([loadApis(), loadDetail(detail.api_id)]);
    } catch (err: any) {
      setOauthError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save credentials');
    } finally {
      setCredentialSaving(false);
    }
  };

  const handleOAuthConnect = async () => {
    if (!detail?.api_id) return;
    if (credentialForm.grant_type === 'client_credentials') {
      await handleSaveCredentials();
      return;
    }
    if (!credentialForm.client_id.trim() || !credentialForm.client_secret.trim()) {
      setOauthError('Client ID and Client Secret are required.');
      return;
    }
    try {
      setOauthConnecting(true);
      setOauthError(null);
      const redirectUri = buildIngestOauthRedirectUri(window.location.origin);
      const resp = await apiOAuthApi.start({
        api_id: detail.api_id,
        provider: credentialForm.provider.trim() || detail.provider || 'generic',
        client_id: credentialForm.client_id.trim(),
        client_secret: credentialForm.client_secret.trim(),
        authorization_url: credentialForm.authorization_url.trim() || undefined,
        token_url: credentialForm.token_url.trim() || undefined,
        grant_type: credentialForm.grant_type,
        scopes: credentialForm.scopes.trim()
          ? credentialForm.scopes.split(/[,\s]+/).filter(Boolean)
          : undefined,
        redirect_uri: redirectUri,
      });
      if (resp.data?.saved && !resp.data?.authorization_url) {
        setCredentialMessage(resp.data?.instruction || 'Credentials stored. No browser redirect is used for this grant.');
        await Promise.all([loadApis(), loadDetail(detail.api_id)]);
        return;
      }
      const authUrl = resp.data?.authorization_url;
      if (!authUrl) throw new Error('No authorization URL returned');
      window.open(authUrl, '_blank', 'noopener,noreferrer');
      const before = {
        status: detail.status,
        updated_at: detail.updated_at || null,
        has_access_token: Boolean(detail.credential_presence?.has_access_token),
      };
      const pollInterval = window.setInterval(async () => {
        try {
          const updated = await apiManagerApi.getWorkspaceApiDetail(detail.api_id, {});
          const next = updated.data?.detail || {};
          if (oauthPollSettled(before, {
            status: next.status,
            updated_at: next.updated_at || null,
            has_access_token: Boolean(next.credential_presence?.has_access_token),
          })) {
            window.clearInterval(pollInterval);
            await Promise.all([loadApis(), loadDetail(detail.api_id)]);
          }
        } catch { /* ignore poll errors */ }
      }, 3000);
      window.setTimeout(() => window.clearInterval(pollInterval), 5 * 60 * 1000);
    } catch (err: any) {
      setOauthError(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to start OAuth flow');
    } finally {
      setOauthConnecting(false);
    }
  };

  const addApiApps = Array.isArray(appsQuery.data?.apps) ? appsQuery.data!.apps : [];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: allApps ? '1fr' : '320px 1fr' }, gap: 2, minHeight: 700 }}>
      {!(allApps && selectedApiId) && (
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: allApps ? undefined : 700 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">API Workspace</Typography>
            <Typography variant="body2" color="text.secondary">
              {allApps
                ? 'Every API you may see, across apps. Choose a card to open it.'
                : 'Default: live + broken. Pending OAuth stubs and retired feeds stay off this list.'}
            </Typography>
          </Box>
          <Tooltip title="Refresh APIs">
            <IconButton onClick={loadApis} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {allApps && error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FormControl size="small" sx={{ mb: 2, maxWidth: allApps ? 320 : undefined }}>
          <InputLabel>Apps</InputLabel>
          <Select
            value={allApps ? 'all' : 'app'}
            label="Apps"
            onChange={(e) => handleScopeChange(e.target.value === 'all' ? 'all' : 'app')}
          >
            <MenuItem value="app">{scopedAppId ? `This app (${scopedAppName || scopedAppId})` : 'Selected app'}</MenuItem>
            <MenuItem value="all">All apps</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          startIcon={<SettingsSuggestIcon />}
          onClick={handleAddApi}
          sx={{ mb: 2, alignSelf: allApps ? 'flex-start' : undefined }}
        >
          Create or configure API
        </Button>

        <TextField
          size="small"
          label="Search APIs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, maxWidth: allApps ? 360 : undefined }}
        />

        <FormControl size="small" sx={{ minWidth: 150, mb: 1.5, maxWidth: allApps ? 240 : undefined }}>
          <InputLabel>Group</InputLabel>
          <Select value={groupFilter} label="Group" onChange={(e) => setGroupFilter(String(e.target.value))}>
            <MenuItem value="all">All groups</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group.name} value={group.name}>
                {group.name} ({group.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {(['live', 'broken', 'pending_auth', 'retired'] as IngestLane[]).map((lane) => {
            const selected = laneFilter.includes(lane);
            return (
              <Chip
                key={lane}
                size="small"
                label={`${INGEST_LANE_LABEL[lane]} (${laneCounts[lane]})`}
                color={selected ? laneChipColor(lane) : 'default'}
                variant={selected ? 'filled' : 'outlined'}
                onClick={() => {
                  setLaneFilter((current) => {
                    const next = current.filter((item) => item !== 'all') as IngestLane[];
                    if (next.includes(lane)) {
                      const remaining = next.filter((item) => item !== lane);
                      return remaining.length ? remaining : [...DEFAULT_INGEST_LANES];
                    }
                    return [...next, lane];
                  });
                }}
              />
            );
          })}
          <Chip
            size="small"
            label={`All (${laneCounts.all})`}
            variant={laneFilter.includes('all' as IngestLane) ? 'filled' : 'outlined'}
            onClick={() => setLaneFilter(['all'])}
          />
        </Stack>

        {loading ? (
          <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : allApps ? (
          <ApiRegistryCards rows={visibleApis} onSelect={setSelectedApiId} />
        ) : groupedApis.length === 0 ? (
          <Alert severity="info">No APIs matched the current filters.</Alert>
        ) : (
          <Box sx={{ overflow: 'auto', pr: 0.5 }}>
            {groupedApis.map(([groupName, items]) => (
              <Box key={groupName} sx={{ mb: 2.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">{groupName}</Typography>
                  <Chip label={items.length} size="small" />
                </Stack>
                <Stack spacing={1}>
                  {items.map((api) => {
                    const selected = api.api_id === selectedApiId;
                    return (
                      <Card
                        key={api.api_id}
                        variant="outlined"
                        onClick={() => setSelectedApiId(api.api_id)}
                        sx={{
                          cursor: 'pointer',
                          borderColor: selected ? 'primary.main' : 'divider',
                          bgcolor: selected ? 'action.selected' : undefined,
                        }}
                      >
                        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                          <Typography variant="subtitle2">{api.api_name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {api.provider || 'Unknown provider'}
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                            <Chip size="small" label={api.auth_type || 'unknown auth'} variant="outlined" />
                            <Chip
                              size="small"
                              label={api.runtime?.lane ? INGEST_LANE_LABEL[api.runtime.lane] : (api.status || 'unknown')}
                              color={api.runtime?.lane ? laneChipColor(api.runtime.lane) : 'default'}
                            />
                            {leftoverOwnerChipLabel(api.leftover_owner) && (
                              <Chip
                                size="small"
                                label={leftoverOwnerChipLabel(api.leftover_owner)}
                                variant="outlined"
                                color="warning"
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                            Last ingest: {formatDateTime(api.runtime?.latest_ingest_at)}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      )}

      {(!allApps || selectedApiId) && (
      <Paper variant="outlined" sx={{ p: 2, minHeight: 700 }}>
        {allApps && (
          <Button variant="text" onClick={() => setSelectedApiId(null)} sx={{ mb: 1 }}>
            All APIs
          </Button>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {detailLoading ? (
          <Box sx={{ py: 16, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !detail ? (
          <Box sx={{ py: 10 }}>
            <Typography variant="h6" gutterBottom>
              Select an API
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose an API from the {allApps ? 'table' : 'left'} to inspect setup, runtime, history, and live data flow.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="h5">{detail.api_name}</Typography>
                  <Chip label={detail.workspace?.effective_group || 'General'} />
                  <Chip
                    label={detail.runtime?.lane ? INGEST_LANE_LABEL[detail.runtime.lane] : (detail.status || 'draft')}
                    color={detail.runtime?.lane ? laneChipColor(detail.runtime.lane) : detail.status === 'active' ? 'success' : 'default'}
                  />
                  {leftoverOwnerChipLabel(detail.leftover_owner) && (
                    <Chip
                      label={leftoverOwnerChipLabel(detail.leftover_owner)}
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {isOnDemand(detail) && <Chip label={ON_DEMAND_LABEL} variant="outlined" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {detail.provider || 'Unknown provider'} • {detail.base_url || 'No base URL captured'}
                </Typography>
                {detail.app_id && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Owning app: {detail.registry?.owner?.app_name || detail.app_id}{' '}
                    <span style={{ fontFamily: 'monospace' }}>{detail.app_id}</span>
                  </Typography>
                )}
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {!allApps && (
                  <Button variant="outlined" startIcon={<EditIcon />} onClick={openEditDialog}>
                    Edit
                  </Button>
                )}
                <Button variant="outlined" startIcon={<SettingsSuggestIcon />} onClick={() => onResumeApi(detail.api_id)}>
                  {String(detail.status || '').startsWith('pending') ? 'Finish setup' : 'Open setup chat'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  component={RouterLink}
                  to={`/api-observe?api_id=${encodeURIComponent(detail.api_id)}`}
                >
                  Open in API Observe
                </Button>
                {!allApps && (
                  <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDeleteApi}>
                    Delete
                  </Button>
                )}
              </Stack>
            </Stack>
            {lastReceipt && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setLastReceipt(null)}>
                Saved. Door receipt <span style={{ fontFamily: 'monospace' }}>{lastReceipt}</span>
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Last success</Typography>
                    <Typography variant="h6">{formatDateTime(detail.runtime?.latest_ingest_at)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Last attempt: {formatDateTime(detail.runtime?.last_attempt_at)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      {presentPipelineKpi(detail.runtime).caption}
                    </Typography>
                    <Typography variant="h6">{presentPipelineKpi(detail.runtime).value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Records last 24h · {detail.runtime?.census_store || 'api_ingest_history'}
                    </Typography>
                    <Typography variant="h6">{detail.runtime?.records_24h ?? detail.runtime?.published_count_window ?? 0}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Same store as Data Flow 24h census
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">Last error</Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontSize: '1rem' }}
                      color={lastErrorLabel !== 'None' ? 'error' : undefined}
                    >
                      {lastErrorLabel}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Registry status: {detail.runtime?.registry_status || detail.status || '—'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Overview</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  <Typography variant="body2" color="text.secondary">
                    {detail.description || 'No friendly description captured yet.'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`Auth: ${detail.auth_type || 'unknown'}`} variant="outlined" />
                    {(detail.workspace?.tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" />
                    ))}
                  </Stack>
                  <Typography variant="body2">
                    <strong>Trigger:</strong> {isOnDemand(detail) ? ON_DEMAND_LABEL : 'scheduled'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Credentials:</strong>{' '}
                    {detail.registry?.credential_state ? CREDENTIAL_STATE_LABEL[detail.registry.credential_state] : '—'}
                    {detail.registry?.credential_state === 'in_vault' && detail.registry?.credential_outside_vault
                      ? ' (also outside Vault)'
                      : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Connected accounts:</strong> {detail.registry?.connected_accounts?.count ?? 0}
                    {detail.registry?.connected_accounts?.names?.length
                      ? ` · ${detail.registry.connected_accounts.names.join(', ')}`
                      : ''}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Classification:</strong>{' '}
                    {detail.classification_template_ids?.length
                      ? detail.classification_template_ids.join(', ')
                      : 'Not recorded'}
                  </Typography>
                  <Typography variant="body2"><strong>Linked asset:</strong> Not recorded</Typography>
                  <Typography variant="body2">
                    <strong>Ingest session:</strong>{' '}
                    {detail.ingest_session ? (
                      <>
                        <span style={{ fontFamily: 'monospace' }}>{detail.ingest_session.session_id}</span>
                        {detail.ingest_session.phase ? ` · ${detail.ingest_session.phase}` : ''}{' '}
                        <Button size="small" onClick={() => onResumeApi(detail.api_id)}>Resume</Button>
                      </>
                    ) : 'None'}
                  </Typography>
                  <Typography variant="body2"><strong>Owner:</strong> {detail.workspace?.owner || 'Unassigned'}</Typography>
                  <Typography variant="body2"><strong>Criticality:</strong> {detail.workspace?.criticality || 'Not set'}</Typography>
                  {detail.workspace?.notes && (
                    <Typography variant="body2"><strong>Notes:</strong> {detail.workspace.notes}</Typography>
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle1">Setup</Typography>
                  <Chip
                    label={setupStatusLabel({
                      provider: detail.provider,
                      apiName: detail.api_name,
                      apiId: detail.api_id,
                      baseUrl: detail.base_url,
                      authType: detail.auth_type,
                      presence: detail.credential_presence,
                      requestHeaders: detail.request_headers,
                    })}
                    color={
                      looksLikeMetNorway({
                        provider: detail.provider,
                        apiName: detail.api_name,
                        apiId: detail.api_id,
                        baseUrl: detail.base_url,
                      })
                        ? (detail.request_headers?.user_agent_persisted ? 'default' : 'warning')
                        : detail.credential_presence?.missing_reason ? 'warning' : detail.credential_presence?.has_api_key || detail.credential_presence?.can_client_credentials || detail.credential_presence?.has_access_token ? 'default' : 'warning'
                    }
                    size="small"
                    icon={<LockIcon />}
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {looksLikeMetNorway({
                  provider: detail.provider,
                  apiName: detail.api_name,
                  apiId: detail.api_id,
                  baseUrl: detail.base_url,
                }) && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>User-Agent</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      MET Norway / YR require a contact User-Agent and no API key. This value is identification, not a secret.
                    </Typography>
                    <TextField
                      size="small"
                      label="User-Agent"
                      fullWidth
                      value={userAgent}
                      onChange={(e) => setUserAgent(e.target.value)}
                      placeholder={MET_NORWAY_USER_AGENT}
                      helperText={
                        detail.request_headers?.user_agent_persisted
                          ? 'Stored on the card as a non-secret header.'
                          : 'Defaults to DevCockpit/Farmspace (https://devcockpit.ai). Save to persist it on this card.'
                      }
                    />
                    {headerError && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        {headerError}
                      </Alert>
                    )}
                    {headerMessage && (
                      <Alert severity="success" sx={{ mt: 1.5 }}>
                        {headerMessage}
                      </Alert>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={headerSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveHeaders}
                        disabled={headerSaving}
                      >
                        {headerSaving ? 'Saving…' : 'Save User-Agent'}
                      </Button>
                    </Stack>
                  </Box>
                )}
                {detail.provider_metadata?.api_ingest?.connector && detail.provider_metadata.api_ingest.sync_endpoint_id && (
                  <ConnectorSyncCard
                    apiId={detail.api_id}
                    meta={detail.provider_metadata.api_ingest}
                    pipelines={detail.pipelines || []}
                    hasCredentials={Boolean(
                      detail.credential_presence
                      && !detail.credential_presence.missing_reason
                      && (detail.credential_presence.has_api_key
                        || detail.credential_presence.has_access_token
                        || detail.credential_presence.can_client_credentials
                        || detail.credential_presence.has_vault_path),
                    )}
                    onChanged={() => loadDetail(detail.api_id)}
                  />
                )}
                {(detail.auth_type === 'oauth2' || detail.auth_type === 'api_key' || detail.auth_type === 'bearer') && (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <LockIcon fontSize="small" />
                      <Typography variant="subtitle2">Credentials</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {detail.auth_type === 'oauth2' && credentialForm.grant_type === 'client_credentials'
                        ? 'This provider uses a machine token (client_credentials). Save Client ID, Client Secret, Token URL, and optional x-api-key. There is no browser consent screen.'
                        : detail.auth_type === 'oauth2'
                          ? 'Authorization-code providers can Connect in a browser. You can also save the client fields without opening a consent screen.'
                          : 'Paste the API key and Save. The full key is never shown again after save.'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                      Status: {credentialStatusLabel(detail.credential_presence, detail.auth_type)}
                      {detail.auth_type === 'api_key' && ` · preview ${presentSecretPreview(null, Boolean(detail.credential_presence?.has_api_key))}`}
                    </Typography>

                    {(detail.auth_type === 'api_key' || detail.auth_type === 'bearer') && (
                      <TextField
                        size="small"
                        label={detail.auth_type === 'bearer' ? 'Bearer token' : 'API key'}
                        fullWidth
                        type={showApiKey ? 'text' : 'password'}
                        value={credentialForm.api_key}
                        onChange={(e) => setCredentialForm((f) => ({ ...f, api_key: e.target.value }))}
                        placeholder={detail.credential_presence?.has_api_key ? 'Enter a new value to rotate' : 'paste key'}
                        helperText={detail.credential_presence?.has_api_key ? 'A key is already stored. Enter a new value only to rotate it.' : 'Required to persist this feed.'}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                                {showApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}

                    {detail.auth_type === 'oauth2' && (
                      <Stack spacing={1.5}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Grant type</InputLabel>
                          <Select
                            label="Grant type"
                            value={credentialForm.grant_type}
                            onChange={(e) => setCredentialForm((f) => ({
                              ...f,
                              grant_type: e.target.value as CredentialFormState['grant_type'],
                            }))}
                          >
                            <MenuItem value="client_credentials">client_credentials (no browser)</MenuItem>
                            <MenuItem value="authorization_code">authorization_code (browser Connect)</MenuItem>
                          </Select>
                        </FormControl>
                        {ingestOauthRedirectUri && (
                          <TextField
                            size="small"
                            label="Redirect URI / Callback URL"
                            fullWidth
                            value={ingestOauthRedirectUri}
                            helperText="Register this exact URI at the provider. CONNECT sends the same value."
                            inputProps={{
                              readOnly: true,
                              'data-testid': 'ingest-oauth-redirect-uri',
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    edge="end"
                                    title="Copy redirect URI"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(ingestOauthRedirectUri);
                                    }}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            size="small"
                            label="Client ID"
                            fullWidth
                            value={credentialForm.client_id}
                            onChange={(e) => setCredentialForm((f) => ({ ...f, client_id: e.target.value }))}
                            placeholder="your-client-id"
                          />
                          <TextField
                            size="small"
                            label="Client Secret"
                            fullWidth
                            type={showClientSecret ? 'text' : 'password'}
                            value={credentialForm.client_secret}
                            onChange={(e) => setCredentialForm((f) => ({ ...f, client_secret: e.target.value }))}
                            placeholder={detail.credential_presence?.has_client_secret ? 'Enter a new value to rotate' : 'your-client-secret'}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton size="small" onClick={() => setShowClientSecret((v) => !v)} edge="end">
                                    {showClientSecret ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          {credentialForm.grant_type === 'authorization_code' && (
                            <TextField
                              size="small"
                              label="Authorization URL"
                              fullWidth
                              value={credentialForm.authorization_url}
                              onChange={(e) => setCredentialForm((f) => ({ ...f, authorization_url: e.target.value }))}
                              placeholder="https://provider.example/oauth/authorize"
                              helperText="Required only for browser Connect"
                            />
                          )}
                          <TextField
                            size="small"
                            label="Token URL"
                            fullWidth
                            value={credentialForm.token_url}
                            onChange={(e) => setCredentialForm((f) => ({ ...f, token_url: e.target.value }))}
                            placeholder="https://provider.example/oauth/token"
                            helperText="Leave blank to use provider defaults"
                          />
                        </Stack>
                        <TextField
                          size="small"
                          label="x-api-key (optional, stored with OAuth)"
                          fullWidth
                          type={showApiKey ? 'text' : 'password'}
                          value={credentialForm.x_api_key}
                          onChange={(e) => setCredentialForm((f) => ({ ...f, x_api_key: e.target.value }))}
                          placeholder={detail.credential_presence?.has_api_key ? 'Enter a new value to rotate' : 'third AfriGIS string, if issued'}
                          helperText={detail.credential_presence?.has_api_key ? `API key ${presentSecretPreview(null, true)}` : 'Some providers send this header in addition to the bearer token.'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                                  {showApiKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                          <TextField
                            size="small"
                            label="Scopes (comma-separated)"
                            fullWidth
                            value={credentialForm.scopes}
                            onChange={(e) => setCredentialForm((f) => ({ ...f, scopes: e.target.value }))}
                            placeholder="optional"
                          />
                          <TextField
                            size="small"
                            label="Provider key (optional name)"
                            fullWidth
                            value={credentialForm.provider}
                            onChange={(e) => setCredentialForm((f) => ({ ...f, provider: e.target.value }))}
                            placeholder={detail.provider || 'provider'}
                            helperText="Name only — not a secret"
                          />
                        </Stack>
                      </Stack>
                    )}

                    {oauthError && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        {oauthError}
                      </Alert>
                    )}
                    {credentialMessage && (
                      <Alert severity="success" sx={{ mt: 1.5 }}>
                        {credentialMessage}
                      </Alert>
                    )}

                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={credentialSaving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                        onClick={handleSaveCredentials}
                        disabled={credentialSaving}
                      >
                        {credentialSaving ? 'Saving…' : 'Save credentials'}
                      </Button>
                      {detail.auth_type === 'oauth2' && credentialForm.grant_type === 'authorization_code' && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={oauthConnecting ? <CircularProgress size={14} color="inherit" /> : <OpenInNewIcon />}
                          onClick={handleOAuthConnect}
                          disabled={oauthConnecting || !credentialForm.client_id.trim() || !credentialForm.client_secret.trim()}
                        >
                          {oauthConnecting ? 'Opening consent screen…' : 'Connect OAuth2'}
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Endpoints</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Runtime joins base URL + path. Edit path if mint stored a product root (credentials stay untouched).
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell>Path</TableCell>
                      <TableCell>Upstream URL</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.endpoints || []).slice(0, 10).map((endpoint) => (
                      <TableRow key={endpoint.endpoint_id}>
                        <TableCell>{endpoint.endpoint_id}</TableCell>
                        <TableCell>{endpoint.method || '-'}</TableCell>
                        <TableCell>{endpoint.path || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                            {composeWorkspaceEndpointUrl(detail.base_url, endpoint.path)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => setPathEdit({
                              endpoint_id: String(endpoint.endpoint_id || ''),
                              path: String(endpoint.path || ''),
                            })}
                          >
                            Edit path
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Connections</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  App/user BYOK rows only. Tenant-shared keys saved above live on the API record (and Vault when available), not in this table.
                </Typography>
                {(detail.connections || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No app/user connections stored.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Expires</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.connections!.map((connection) => (
                        <TableRow key={connection.connection_id}>
                          <TableCell>{connection.connection_id}</TableCell>
                          <TableCell>{connection.status || '-'}</TableCell>
                          <TableCell>{formatDateTime(connection.expires_at)}</TableCell>
                          <TableCell align="right">
                            <IconButton color="error" onClick={() => handleDeleteConnection(connection.connection_id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Mappings</Typography>
                {(detail.mappings || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No field mappings captured.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Endpoint</TableCell>
                        <TableCell>Template</TableCell>
                        <TableCell>Fields</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.mappings!.map((mapping) => (
                        <TableRow key={mapping.mapping_id}>
                          <TableCell>{mapping.endpoint_id}</TableCell>
                          <TableCell>{mapping.template_id}</TableCell>
                          <TableCell>{Array.isArray(mapping.mappings) ? mapping.mappings.length : 0}</TableCell>
                          <TableCell align="right">
                            <IconButton color="error" onClick={() => handleDeleteMapping(mapping.mapping_id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Runtime</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(detail.pipelines || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No scheduled pipeline objects for this API. Ingest still writes api_ingest_history — that is the store Data Flow uses for 24h records, not a missing ingest.
                  </Typography>
                ) : (
                  <>
                    <FormControl size="small" sx={{ minWidth: 260, mb: 2 }}>
                      <InputLabel>Live pipeline</InputLabel>
                      <Select
                        label="Live pipeline"
                        value={selectedPipelineId || ''}
                        onChange={(e) => {
                          const value = String(e.target.value || '');
                          setSelectedPipelineId(value || null);
                          onPipelineSelect?.(value || null);
                        }}
                      >
                        {detail.pipelines!.map((pipeline) => (
                          <MenuItem key={pipeline.pipeline_id} value={pipeline.pipeline_id}>
                            {pipeline.name || pipeline.pipeline_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Purpose</TableCell>
                          <TableCell>Schedule</TableCell>
                          <TableCell>Settings</TableCell>
                          <TableCell>Last error</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.pipelines!.map((pipeline) => {
                          const onDemand = isOnDemand(pipeline) || isOnDemand(detail);
                          const draft = draftFor(pipeline);
                          const busy = pipelineSaving === pipeline.pipeline_id;
                          const seconds = Number(draft.every_seconds);
                          const intervalChanged = draft.every_seconds !== String(pipeline.schedule?.every_seconds || '');
                          const storeChanged = draft.store_mode !== String(pipeline.store_mode || 'telemetry');
                          return (
                          <TableRow key={pipeline.pipeline_id}>
                            <TableCell>{pipeline.name || pipeline.pipeline_id}</TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {pipeline.purpose === 'instance_ingest' ? 'instance ingest' : 'health ping'}
                              </Typography>
                              <Typography variant="caption" display="block" color="text.secondary">
                                {pipeline.enabled === false || pipeline.purpose !== 'instance_ingest'
                                  ? 'Not always-on customer ingest'
                                  : `Every ${pipeline.schedule?.every_seconds || 300}s`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {onDemand
                                ? ON_DEMAND_LABEL
                                : pipeline.purpose === 'instance_ingest' && pipeline.enabled !== false
                                  ? `Every ${pipeline.schedule?.every_seconds || 300}s`
                                  : 'Off until instance requests'}
                            </TableCell>
                            <TableCell>
                              {onDemand ? (
                                <Typography variant="caption" color="text.secondary">
                                  {pipeline.enabled === false ? 'disabled' : 'enabled'} · store {pipeline.store_mode || 'telemetry'} · the app runs it
                                </Typography>
                              ) : (
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={busy}
                                    onClick={() => savePipeline(pipeline.pipeline_id, { enabled: pipeline.enabled === false })}
                                  >
                                    {pipeline.enabled === false ? 'Enable' : 'Disable'}
                                  </Button>
                                  <TextField
                                    size="small"
                                    label="Every (s)"
                                    type="number"
                                    sx={{ width: 110 }}
                                    value={draft.every_seconds}
                                    onChange={(e) => setPipelineDraft((current) => ({
                                      ...current,
                                      [pipeline.pipeline_id]: { ...draft, every_seconds: e.target.value },
                                    }))}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 130 }}>
                                    <InputLabel>Store</InputLabel>
                                    <Select
                                      label="Store"
                                      value={draft.store_mode}
                                      onChange={(e) => setPipelineDraft((current) => ({
                                        ...current,
                                        [pipeline.pipeline_id]: { ...draft, store_mode: String(e.target.value) },
                                      }))}
                                    >
                                      {STORE_MODES.map((mode) => (
                                        <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <Button
                                    size="small"
                                    disabled={busy || (!intervalChanged && !storeChanged) || (intervalChanged && !(seconds >= 5))}
                                    onClick={() => savePipeline(pipeline.pipeline_id, {
                                      ...(intervalChanged ? { schedule: { every_seconds: seconds } } : {}),
                                      ...(storeChanged ? { store_mode: draft.store_mode } : {}),
                                    })}
                                  >
                                    Save
                                  </Button>
                                </Stack>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="caption"
                                color={
                                  presentPipelineLastError(pipeline) !== '—'
                                    ? 'error'
                                    : 'text.secondary'
                                }
                              >
                                {presentPipelineLastError(pipeline)}
                              </Typography>
                              {pipeline.last_health_ping_at && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Last health ping {formatDateTime(pipeline.last_health_ping_at)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {onDemand ? (
                                <Typography variant="caption" color="text.secondary">No portal runs</Typography>
                              ) : (
                                <Stack direction="row" spacing={1} justifyContent="flex-end">
                                  <Button
                                    size="small"
                                    onClick={() => handleRunPipeline(pipeline.pipeline_id, 'health_ping')}
                                  >
                                    Health ping
                                  </Button>
                                  <Button
                                    size="small"
                                    startIcon={<PlayArrowIcon />}
                                    onClick={() => handleRunPipeline(pipeline.pipeline_id, 'instance_ingest')}
                                  >
                                    Run ingest
                                  </Button>
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    {selectedPipeline && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2">Instance request.query</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          App-instance lat/lon (or other query keys). Empty on purpose. Health-ping coords are not a default here and are not customer data.
                        </Typography>
                        {queryRows.map((row, index) => (
                          <Stack key={`${row.key}-${index}`} direction="row" spacing={1} sx={{ mb: 1 }}>
                            <TextField
                              size="small"
                              label="Key"
                              value={row.key}
                              onChange={(e) => {
                                const next = [...queryRows];
                                next[index] = { ...next[index], key: e.target.value };
                                setQueryRows(next);
                              }}
                            />
                            <TextField
                              size="small"
                              label="Value"
                              value={row.value}
                              onChange={(e) => {
                                const next = [...queryRows];
                                next[index] = { ...next[index], value: e.target.value };
                                setQueryRows(next);
                              }}
                            />
                          </Stack>
                        ))}
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            onClick={() => setQueryRows([...queryRows, { key: '', value: '' }])}
                          >
                            Add query field
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={querySaving}
                            onClick={handleSavePipelineQuery}
                          >
                            {querySaving ? 'Saving…' : 'Save instance query'}
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">History & replay</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(detail.history || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No rows in api_ingest_history for this API in this view.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Created</TableCell>
                        <TableCell>Ingest ID</TableCell>
                        <TableCell>Endpoint</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.history!.map((item) => (
                        <TableRow key={item.ingest_id}>
                          <TableCell>{formatDateTime(item.created_at)}</TableCell>
                          <TableCell>
                            <code style={{ fontSize: 12, wordBreak: 'break-all' }}>{item.ingest_id || '—'}</code>
                          </TableCell>
                          <TableCell>{item.endpoint_id || '-'}</TableCell>
                          <TableCell>{item.status || '-'}</TableCell>
                          <TableCell>{item.processing_time_ms ? `${item.processing_time_ms} ms` : '-'}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handleViewObservation(item.ingest_id)}
                                disabled={!item.ingest_id}
                              >
                                View
                              </Button>
                              <Button
                                size="small"
                                startIcon={<ReplayIcon />}
                                onClick={() => handleReplay(item.ingest_id)}
                              >
                                Replay
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Live ingest</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <LiveIngestPanel pipelineId={selectedPipelineId} onPipelineChange={(id) => {
                  setSelectedPipelineId(id || null);
                  onPipelineSelect?.(id || null);
                }} />
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Paper>
      )}

      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ingest observation</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="body2">
              Record id:{' '}
              <code style={{ wordBreak: 'break-all' }}>{viewIngestId || '—'}</code>
            </Typography>
            {viewLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {viewError && <Alert severity="error">{viewError}</Alert>}
            {!viewLoading && !viewError && viewObservation && (
              <>
                {viewObservation.present && viewObservation.body != null ? (
                  <>
                    {viewObservation.note && (
                      <Typography variant="caption" color="text.secondary">
                        {viewObservation.note}
                      </Typography>
                    )}
                    {viewObservation.source && (
                      <Typography variant="caption" color="text.secondary">
                        Source: {viewObservation.source}
                      </Typography>
                    )}
                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.5,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                        overflow: 'auto',
                        fontSize: 12,
                      }}
                    >
                      {typeof viewObservation.body === 'string'
                        ? viewObservation.body
                        : JSON.stringify(viewObservation.body, null, 2)}
                    </Box>
                  </>
                ) : (
                  <Alert severity="info">
                    {viewObservation.empty_message
                      || 'No observation body is persisted on this history record. The store kept ingest metadata and (when present) a Pulsar receipt only.'}
                  </Alert>
                )}
                {viewObservation.pulsar_receipt != null && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Pulsar receipt:{' '}
                    <code>{JSON.stringify(viewObservation.pulsar_receipt)}</code>
                  </Typography>
                )}
                {viewObservation.timescale != null && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    Timescale: <code>{JSON.stringify(viewObservation.timescale)}</code>
                  </Typography>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addApiOpen} onClose={() => setAddApiOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add API to which app?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The new API belongs to the app you pick. The header switches to that app and the setup assistant opens.
          </Typography>
          {appsQuery.isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>App</InputLabel>
              <Select label="App" value={addApiAppId} onChange={(e) => setAddApiAppId(String(e.target.value))}>
                {addApiApps.map((app: any) => (
                  <MenuItem key={app.app_id} value={app.app_id}>{app.name || app.app_id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddApiOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!addApiApps.some((app: any) => app.app_id === addApiAppId)}
            onClick={confirmAddApi}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit API workspace metadata</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="API name" value={editForm.api_name} onChange={(e) => setEditForm((prev) => ({ ...prev, api_name: e.target.value }))} />
            <TextField label="Provider" value={editForm.provider} onChange={(e) => setEditForm((prev) => ({ ...prev, provider: e.target.value }))} />
            <TextField label="Base URL" value={editForm.base_url} onChange={(e) => setEditForm((prev) => ({ ...prev, base_url: e.target.value }))} />
            <FormControl>
              <InputLabel>Status</InputLabel>
              <Select value={editForm.status} label="Status" onChange={(e) => setEditForm((prev) => ({ ...prev, status: String(e.target.value) }))}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Group override" value={editForm.group_override} onChange={(e) => setEditForm((prev) => ({ ...prev, group_override: e.target.value }))} helperText="Leave blank to keep the auto-suggested group." />
            <TextField label="Tags" value={editForm.tags} onChange={(e) => setEditForm((prev) => ({ ...prev, tags: e.target.value }))} helperText="Comma separated labels." />
            <TextField label="Owner" value={editForm.owner} onChange={(e) => setEditForm((prev) => ({ ...prev, owner: e.target.value }))} />
            <TextField label="Criticality" value={editForm.criticality} onChange={(e) => setEditForm((prev) => ({ ...prev, criticality: e.target.value }))} />
            <TextField label="Notes" value={editForm.notes} onChange={(e) => setEditForm((prev) => ({ ...prev, notes: e.target.value }))} multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveMetadata} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pathEdit)} onClose={() => setPathEdit(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit endpoint path</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Changes only <code>endpoints[].path</code>. Tokens and catalog credentials are not sent.
            </Typography>
            <TextField
              label="Path"
              value={pathEdit?.path || ''}
              onChange={(e) => setPathEdit((prev) => (prev ? { ...prev, path: e.target.value } : prev))}
              helperText={`Joins to ${composeWorkspaceEndpointUrl(detail?.base_url, pathEdit?.path)}`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPathEdit(null)}>Cancel</Button>
          <Button onClick={handleSaveEndpointPath} variant="contained" disabled={pathSaving || !pathEdit?.path?.trim()}>
            {pathSaving ? 'Saving...' : 'Save path'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
